// src/pages/Admin/UserAdmin/EntraUsersPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Container, Form, InputGroup, Pagination, Spinner, Table } from 'react-bootstrap';
import { FaExternalLinkAlt, FaSearch, FaSort, FaSortDown, FaSortUp, FaSyncAlt, FaUsers } from 'react-icons/fa';
import { EntraUser, getEntraUsers, getUserDirectoryFilterOptions, PagedResult } from '@/api/userAdminApi';
import './SpAccountPurgePage.scss';

const PAGE_SIZE = 25;
type SortDirection = 'asc' | 'desc';

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');

function PaginationBar({
  page, pageSize, total, onChange,
}: {
  page: number; pageSize: number; total: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {page > 2 && <Pagination.Item onClick={() => onChange(1)}>1</Pagination.Item>}
        {page > 3 && <Pagination.Ellipsis />}
        {page > 1 && <Pagination.Item onClick={() => onChange(page - 1)}>{page - 1}</Pagination.Item>}
        <Pagination.Item active>{page}</Pagination.Item>
        {page < totalPages && <Pagination.Item onClick={() => onChange(page + 1)}>{page + 1}</Pagination.Item>}
        {page < totalPages - 2 && <Pagination.Ellipsis />}
        {page < totalPages - 1 && <Pagination.Item onClick={() => onChange(totalPages)}>{totalPages}</Pagination.Item>}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

function SortHeader({
  label, column, sortBy, sortDirection, onSort,
}: {
  label: string;
  column: string;
  sortBy: string;
  sortDirection: SortDirection;
  onSort: (column: string) => void;
}) {
  const active = sortBy === column;
  return (
    <button type="button" className="spa-sort-header" onClick={() => onSort(column)}>
      <span>{label}</span>
      {active ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
    </button>
  );
}

const EntraUsersPage: React.FC = () => {
  const [data, setData] = useState<PagedResult<EntraUser> | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [departmentId, setDepartmentId] = useState('');
  const [submittedDepartmentId, setSubmittedDepartmentId] = useState('');
  const [sortBy, setSortBy] = useState('displayName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const isActive = activeFilter === 'all' ? null : activeFilter === 'active';
      setData(await getEntraUsers(page, PAGE_SIZE, submittedSearch, isActive, submittedDepartmentId, sortBy, sortDirection));
    } catch {
      setError('Failed to load Entra users');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, sortBy, sortDirection, submittedDepartmentId, submittedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    getUserDirectoryFilterOptions()
      .then((options) => { if (!cancelled) setDepartmentOptions(options.entraDepartmentIds || []); })
      .catch(() => { if (!cancelled) setDepartmentOptions([]); });
    return () => { cancelled = true; };
  }, []);

  function applyFilters(e?: React.FormEvent) {
    e?.preventDefault();
    setPage(1);
    setSubmittedSearch(search.trim());
    setSubmittedDepartmentId(departmentId.trim());
  }

  function clearFilters() {
    setSearch('');
    setSubmittedSearch('');
    setDepartmentId('');
    setSubmittedDepartmentId('');
    setActiveFilter('all');
    setPage(1);
  }

  function sort(column: string) {
    setPage(1);
    if (sortBy === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  }

  function openUser(identifier?: string) {
    if (!identifier) return;
    window.open(`/admin/users/details/${encodeURIComponent(identifier)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <Container className="mt-4 spa-page">
      <div className="mb-4">
        <h2 className="mb-1"><FaUsers className="me-2 text-primary" />Entra Users</h2>
        <p className="text-muted mb-0">Full user dump from Entra, including removed-account history.</p>
      </div>

      <Form onSubmit={applyFilters} className="mb-3">
        <div className="d-flex gap-2 flex-wrap align-items-end">
          <Form.Group style={{ minWidth: 320, flex: '1 1 320px' }}>
            <Form.Label className="small text-muted mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, UPN, employee number, manager, hierarchy"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group style={{ width: 170 }}>
            <Form.Label className="small text-muted mb-1">Status</Form.Label>
            <Form.Select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value as typeof activeFilter); setPage(1); }}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Removed</option>
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ width: 190 }}>
            <Form.Label className="small text-muted mb-1">Department ID</Form.Label>
            <Form.Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">All departments</option>
              {departmentOptions.map((dept) => <option key={dept} value={dept}>{dept}</option>)}
            </Form.Select>
          </Form.Group>
          <Button type="submit" variant="primary">Apply</Button>
          <Button type="button" variant="outline-secondary" onClick={clearFilters}>Clear</Button>
          <Button type="button" variant="outline-primary" onClick={load}><FaSyncAlt className="me-1" />Refresh</Button>
        </div>
      </Form>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="spa-table align-middle">
              <thead>
                <tr>
                  <th><SortHeader label="Name" column="displayName" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Email / UPN" column="email" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Employee" column="employeeNum" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Department" column="department" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Manager" column="manager" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th>Org</th>
                  <th><SortHeader label="Status" column="status" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Last Crawl" column="lastCrawl" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Removed" column="removed" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((u) => {
                  const userIdentifier = u.email || u.upn || u.employeeNum;
                  return (
                    <tr key={u.userId} className="spa-click-row" onClick={() => openUser(userIdentifier)}>
                      <td className="fw-semibold text-nowrap">
                        {u.displayName || '-'}
                        <FaExternalLinkAlt className="ms-2 text-muted small" />
                      </td>
                      <td>
                        <div>{u.email || u.upn || '-'}</div>
                        {u.email && u.upn && u.email !== u.upn && <div className="text-muted small">{u.upn}</div>}
                      </td>
                      <td>
                        <div>{u.employeeNum || '-'}</div>
                        {u.employeeWindowsNtid && <div className="text-muted small">{u.employeeWindowsNtid}</div>}
                      </td>
                      <td>
                        <div>{u.employeeDeptId || '-'}</div>
                        {u.managerDeptId && <div className="text-muted small">Mgr dept: {u.managerDeptId}</div>}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 align-baseline"
                          onClick={(e) => { e.stopPropagation(); openUser(u.managerEmpNum || u.managerWindowsNtid); }}
                          disabled={!u.managerEmpNum && !u.managerWindowsNtid}
                        >
                          {u.managerEmpNum || '-'}
                        </button>
                        {u.managerWindowsNtid && <div className="text-muted small">{u.managerWindowsNtid}</div>}
                      </td>
                      <td className="small" style={{ minWidth: 220 }}>
                        <div>{u.orgHierarchy || '-'}</div>
                        {u.orgHierarchyDept && <div className="text-muted">{u.orgHierarchyDept}</div>}
                      </td>
                      <td><Badge bg={u.effEndDt ? 'secondary' : 'success'}>{u.effEndDt ? 'Removed' : 'Active'}</Badge></td>
                      <td className="text-nowrap text-muted">{fmt(u.lastCrawlDt)}</td>
                      <td className="text-nowrap text-muted">{fmt(u.effEndDt)}</td>
                    </tr>
                  );
                })}
                {data?.items.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-muted py-5">No Entra users found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}
    </Container>
  );
};

export default EntraUsersPage;
